/**
 * KNK-1.53 — importing Doors and knock history from another app (migration 20261009a5).
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What the answers mean is `lib/knockImport.ts`; who may do what
 * is the database's. Errors are thrown with the server's own message, because every refusal here
 * is a sentence written for the person reading it.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  parseImportBatches,
  parseImportOverview,
  parseImportPreview,
  presetToSave,
  type CodeMap,
  type ColumnMap,
  type ImportBatch,
  type ImportOverview,
  type ImportPreset,
  type ImportPreview,
  type ImportRow,
} from '@/lib/knockImport';

export interface ImportRequest {
  sourceKey: string;
  fileName: string;
  rows: ImportRow[];
  codes: Record<string, string | null>;
}

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const args = (r: ImportRequest) => ({
  p_source: r.sourceKey,
  p_file_name: r.fileName,
  p_rows: r.rows,
  p_codes: r.codes,
  p_tz: browserZone(),
});

export async function fetchImportOverview(): Promise<ImportOverview> {
  const { data, error } = await supabase.rpc('knock_import_overview');
  if (error) throw new Error(error.message);
  const overview = parseImportOverview(data);
  if (!overview) throw new Error('The import settings came back in a shape this screen does not understand.');
  return overview;
}

/** A dry run: the server runs the real import and rolls it back. Writes nothing. */
export async function previewImport(r: ImportRequest): Promise<ImportPreview> {
  const { data, error } = await supabase.rpc('knock_import_preview', args(r));
  if (error) throw new Error(error.message);
  const preview = parseImportPreview(data);
  if (!preview) throw new Error('The preview came back in a shape this screen does not understand.');
  return preview;
}

/** Imports what was previewed; the server refuses if its counts no longer match. */
export async function applyImport(r: ImportRequest, p: ImportPreview): Promise<ImportBatch[]> {
  const { data, error } = await supabase.rpc('knock_import_apply', {
    ...args(r),
    p_expected_knocks: p.knocksImported,
    p_expected_doors: p.doorsCreated,
  });
  if (error) throw new Error(error.message);
  return parseImportBatches((data as { batches?: unknown } | null)?.batches);
}

export async function revertImport(batchId: string): Promise<ImportBatch[]> {
  const { data, error } = await supabase.rpc('knock_import_revert', { p_batch_id: batchId });
  if (error) throw new Error(error.message);
  return parseImportBatches((data as { batches?: unknown } | null)?.batches);
}

export async function saveImportPreset(preset: ImportPreset, map: ColumnMap, codes: CodeMap): Promise<void> {
  const { error } = await supabase.rpc('knock_import_save_preset', presetToSave(preset, map, codes));
  if (error) throw new Error(error.message);
}
